import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiPatch } from '../api/client.js';
import { CreditCard } from 'lucide-react';
import { useTerminals, useCreateTerminal, useUpdateTerminal, useAssignTerminal, useChangeTerminalMode, useOrphanTerminals, useDeleteTerminal } from '../hooks/useTerminals.js';

// Extrae el modelo desde el ID de terminal (ej: NEWLAND_N950__SERIAL → "Newland N950")
function parseModelFromId(id) {
  if (!id) return '';
  const match = id.match(/^([A-Z0-9]+_[A-Z0-9]+)__/i);
  if (match) return match[1].replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return '';
}
import { usePosByStore } from '../hooks/usePos.js';
import { useStores } from '../hooks/useStores.js';
import { useClientStore } from '../store/client.store.js';
import { useAuthStore } from '../store/auth.store.js';
import Table from '../components/Table.jsx';
import Modal from '../components/Modal.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Badge from '../components/Badge.jsx';
import NoClientSelected from '../components/NoClientSelected.jsx';
import PageHeader from '../components/PageHeader.jsx';

const statusVariant = { active: 'success', disabled: 'danger', maintenance: 'warning' };

function AssignModal({ terminal, open, onClose }) {
  const { data: stores } = useStores();
  const [selectedStore, setSelectedStore] = useState('');
  const { data: posList } = usePosByStore(selectedStore || null);
  const [selectedPos, setSelectedPos] = useState('');
  const assignMutation = useAssignTerminal(terminal?.id);
  const changeMutation = useChangeTerminalMode(terminal?.id);
  const [msg, setMsg] = useState(null);
  const [pdvPrompt, setPdvPrompt] = useState(false);

  if (!open) return null;

  const needsPdv = terminal?.operating_mode !== 'PDV';

  const handleAssign = () => {
    setMsg(null);
    assignMutation.mutate({ posId: selectedPos || null }, {
      onSuccess: () => {
        if (selectedPos && needsPdv) {
          setPdvPrompt(true);
        } else {
          onClose();
        }
      },
      onError: (e) => setMsg(e.response?.data?.message || 'Error al asignar'),
    });
  };

  const handleActivatePdv = () => {
    changeMutation.mutate('PDV', {
      onSuccess: () => { setPdvPrompt(false); onClose(); },
      onError: (e) => {
        const mpMsg = e.response?.data?.details?.errors?.[0]?.message
          || e.response?.data?.message
          || 'Error al activar PDV';
        setMsg(mpMsg);
        setPdvPrompt(false);
        // no onClose — el modal de asignación sigue abierto para que el operador vea el error
      },
    });
  };

  if (pdvPrompt) {
    return (
      <Modal open={open} onClose={() => { setPdvPrompt(false); onClose(); }} title="Activar modo PDV"
        footer={
          <>
            <Button variant="ghost" onClick={() => { setPdvPrompt(false); onClose(); }}>Ahora no</Button>
            <Button loading={changeMutation.isPending} onClick={handleActivatePdv}>Activar PDV</Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          El terminal fue asignado correctamente. Para que procese órdenes integradas necesita estar en modo <strong>PDV</strong> (actualmente en <strong>{terminal?.operating_mode || 'STANDALONE'}</strong>).
        </p>
        <p className="text-sm text-slate-400 mt-2">¿Activar PDV ahora?</p>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={`Asignar terminal ${terminal?.mp_terminal_id}`}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button loading={assignMutation.isPending} onClick={handleAssign}>Asignar</Button></>}
    >
      <div>
        <label className="label">Store</label>
        <select className="input-base" value={selectedStore} onChange={(e) => { setSelectedStore(e.target.value); setSelectedPos(''); }}>
          <option value="">— Seleccionar store —</option>
          {stores?.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.external_id})</option>)}
        </select>
      </div>
      {selectedStore && (
        <div>
          <label className="label">POS</label>
          <select className="input-base" value={selectedPos} onChange={(e) => setSelectedPos(e.target.value)}>
            <option value="">— Sin asignar —</option>
            {posList?.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.external_id})</option>)}
          </select>
        </div>
      )}
      {needsPdv && selectedPos && (
        <p className="text-xs text-amber-400">
          ⚠ Este terminal está en modo {terminal?.operating_mode || 'STANDALONE'}. Al asignar se te ofrecerá activar PDV para habilitar órdenes integradas.
        </p>
      )}
      {msg && <p className="text-sm text-red-400">{msg}</p>}
    </Modal>
  );
}

function EditModal({ terminal, open, onClose }) {
  const [form, setForm] = useState({ mpTerminalId: '', serialNumber: '', model: '' });
  const updateMutation = useUpdateTerminal(terminal?.id);
  const [msg, setMsg] = useState(null);

  // Sincronizar form cuando se abre con un terminal distinto
  if (open && terminal && form.mpTerminalId === '' && terminal.mp_terminal_id) {
    setForm({ mpTerminalId: terminal.mp_terminal_id, serialNumber: terminal.serial_number || '', model: terminal.model || '' });
  }

  if (!open) return null;

  const handleSave = () => {
    setMsg(null);
    updateMutation.mutate(
      { mpTerminalId: form.mpTerminalId, serialNumber: form.serialNumber, model: form.model },
      {
        onSuccess: () => { onClose(); setForm({ mpTerminalId: '', serialNumber: '', model: '' }); },
        onError: (e) => setMsg(e.response?.data?.message || 'Error al guardar'),
      }
    );
  };

  return (
    <Modal open={open} onClose={onClose} title="Editar terminal"
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button loading={updateMutation.isPending} onClick={handleSave}>Guardar</Button></>}
    >
      <Input label="MP Terminal ID *" value={form.mpTerminalId}
        onChange={(e) => setForm(f => ({ ...f, mpTerminalId: e.target.value }))} />
      <Input label="Nro. de serie" value={form.serialNumber}
        onChange={(e) => setForm(f => ({ ...f, serialNumber: e.target.value }))} />
      <Input label="Modelo" value={form.model}
        onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))} />
      {msg && <p className="text-sm text-red-400">{msg}</p>}
    </Modal>
  );
}

export default function Terminals() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  const { data, isLoading } = useTerminals();
  const createMutation = useCreateTerminal();
  const { data: orphans = [], isLoading: orphansLoading } = useOrphanTerminals();
  const [createOpen, setCreateOpen] = useState(!!location.state?.autoOpen);
  const [assignTarget, setAssignTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({
    mpTerminalId: '', serialNumber: '', model: '', operatingMode: 'PDV',
    ...(location.state?.prefillTerminal || {}),
  });
  const [err, setErr] = useState(null);
  const [deleteErr, setDeleteErr] = useState(null);
  const [deleteExportUrl, setDeleteExportUrl] = useState(null);

  if (user?.role === 'superadmin' && !selectedClientId) {
    return <NoClientSelected entity="terminales" />;
  }

  const handleCreate = () => {
    setErr(null);
    const mpPosId = location.state?.prefillTerminal?.mpPosId;
    createMutation.mutate(form, {
      onSuccess: async (created) => {
        if (mpPosId && created?.id) {
          try {
            await apiPatch(`/terminals/${created.id}/assign`, { mpPosId: String(mpPosId) });
          } catch (e) {
            // auto-assign no crítico — terminal registrada igual
          }
        }
        setCreateOpen(false);
        setForm({ mpTerminalId: '', serialNumber: '', model: '', operatingMode: 'PDV' });
      },
      onError: (e) => setErr(e.response?.data?.message || 'Error al crear terminal'),
    });
  };

  const ModeButton = ({ terminal }) => {
    const changeMutation = useChangeTerminalMode(terminal.id);
    const current = terminal.operating_mode;
    const next = current === 'PDV' ? 'STANDALONE' : 'PDV';
    const mpErrMsg = changeMutation.error?.response?.data?.details?.errors?.[0]?.message
      || changeMutation.error?.response?.data?.message
      || changeMutation.error?.message;
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          {current
            ? <Badge variant={current === 'PDV' ? 'success' : 'warning'}>{current}</Badge>
            : <span className="text-slate-500 text-xs">—</span>}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!confirm(`¿Cambiar modo de ${terminal.mp_terminal_id} de ${current || '?'} a ${next}?`)) return;
              changeMutation.mutate(next);
            }}
            className="text-xs text-slate-400 hover:text-accent transition-colors"
            title={`Cambiar a ${next}`}
          >
            {changeMutation.isPending ? '…' : '⇄'}
          </button>
        </div>
        {changeMutation.isError && (
          <span className="text-xs text-red-400 max-w-[180px] leading-tight" title={mpErrMsg}>
            ⚠ {mpErrMsg?.length > 60 ? mpErrMsg.slice(0, 58) + '…' : mpErrMsg}
          </span>
        )}
      </div>
    );
  };

  const columns = [
    { key: 'mp_terminal_id', header: 'Terminal ID', render: (r) => <span className="font-mono text-xs">{r.mp_terminal_id}</span> },
    { key: 'model', header: 'Modelo', render: (r) => r.model || <span className="text-slate-500 text-xs italic">{parseModelFromId(r.mp_terminal_id) || '—'}</span> },
    { key: 'operating_mode', header: 'Modo', render: (r) => <ModeButton terminal={r} /> },
    { key: 'pos_name', header: 'POS asignado', render: (r) => r.pos_name ? `${r.pos_name} (${r.pos_external_id})` : <span className="text-slate-500">Sin asignar</span> },
    { key: 'status', header: 'Estado', render: (r) => <Badge variant={statusVariant[r.status] || 'default'}>{r.status}</Badge> },
    {
      key: 'actions', header: '',
      render: (r) => {
        const deleteMutation = useDeleteTerminal(r.id);
        return (
          <div className="flex gap-1">
            <Button variant="ghost" className="text-xs py-1 px-2" onClick={(e) => { e.stopPropagation(); setEditTarget(r); }}>Editar</Button>
            <Button variant="ghost" className="text-xs py-1 px-2" onClick={(e) => { e.stopPropagation(); setAssignTarget(r); }}>Asignar</Button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!confirm(`¿Eliminar la terminal "${r.mp_terminal_id}"?`)) return;
                setDeleteErr(null); setDeleteExportUrl(null);
                deleteMutation.mutate(undefined, {
                  onError: (err) => {
                    const d = err.response?.data;
                    setDeleteErr(d?.message || d?.error || 'Error al eliminar la terminal');
                    if (d?.export_url) setDeleteExportUrl(d.export_url);
                  },
                });
              }}
              className="text-xs text-red-400 hover:text-red-300 px-2 py-1"
            >
              {deleteMutation.isPending ? '…' : 'Eliminar'}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader icon={CreditCard} title="Terminales" subtitle={data ? `${data.length} registradas` : undefined} color="amber">
        <Button onClick={() => setCreateOpen(true)}>+ Nueva terminal</Button>
      </PageHeader>
      <Table columns={columns} data={data} loading={isLoading} empty="No hay terminales registradas" />
      {deleteErr && (
        <div className="mt-2 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2 flex items-center justify-between gap-4">
          <span>{deleteErr}</span>
          {deleteExportUrl && (
            <a
              href={`${import.meta.env.VITE_API_URL || 'http://localhost:3151/admin'}${deleteExportUrl}`}
              download
              className="text-xs text-amber-400 hover:text-amber-300 whitespace-nowrap underline"
            >
              Descargar órdenes
            </a>
          )}
        </div>
      )}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Nueva terminal"
        footer={<><Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button><Button loading={createMutation.isPending} onClick={handleCreate}>Crear</Button></>}
      >
        {orphansLoading ? (
          <p className="text-xs text-slate-400">Consultando terminales disponibles en MP…</p>
        ) : orphans.length > 0 ? (
          <div>
            <label className="label">Terminales disponibles en MP (sin registrar)</label>
            <select
              className="input-base"
              defaultValue=""
              onChange={(e) => {
                const t = orphans.find(o => o.mp_terminal_id === e.target.value);
                if (!t) return;
                const serial = t.mp_terminal_id.split('__')[1] || '';
                const model  = parseModelFromId(t.mp_terminal_id);
                setForm(f => ({ ...f, mpTerminalId: t.mp_terminal_id, serialNumber: serial, model: f.model || model }));
              }}
            >
              <option value="">— Seleccioná una terminal de MP —</option>
              {orphans.map(o => (
                <option key={o.mp_terminal_id} value={o.mp_terminal_id}>
                  {o.mp_terminal_id}{o.operating_mode ? ` · ${o.operating_mode}` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">Seleccioná para pre-completar los campos, o ingresá el ID manualmente.</p>
          </div>
        ) : (
          <p className="text-xs text-slate-500">No hay terminales en MP sin registrar para este cliente.</p>
        )}
        <Input label="MP Terminal ID *" value={form.mpTerminalId}
          onChange={(e) => {
            const id = e.target.value;
            const detected = parseModelFromId(id);
            setForm(f => ({ ...f, mpTerminalId: id, model: f.model || detected }));
          }}
          placeholder="NEWLAND_N950__..." />
        <p className="text-xs text-slate-500 -mt-1">El modelo se detecta automáticamente del ID si no lo completás</p>
        <Input label="Modelo" value={form.model} onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))} placeholder="Point Smart" />
        <Input label="Nro. de serie" value={form.serialNumber} onChange={(e) => setForm(f => ({ ...f, serialNumber: e.target.value }))} />
        <div>
          <label className="label">Modo de operación</label>
          <select
            className="input-base"
            value={form.operatingMode}
            onChange={(e) => setForm(f => ({ ...f, operatingMode: e.target.value }))}
          >
            <option value="PDV">PDV — Punto de Venta integrado (recomendado)</option>
            <option value="STANDALONE">STANDALONE — Operación manual sin integración</option>
          </select>
          <p className="text-xs text-slate-500 mt-1">
            PDV es requerido para la integración con MercadoPago Point.
          </p>
        </div>
        {err && <p className="text-sm text-red-400">{err}</p>}
      </Modal>
      <AssignModal terminal={assignTarget} open={!!assignTarget} onClose={() => setAssignTarget(null)} />
      <EditModal terminal={editTarget} open={!!editTarget} onClose={() => setEditTarget(null)} />
    </div>
  );
}
